import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '@/lib/api-client';
import type {
  HoldRequest,
  CreateHoldRequestData,
  CreateLineItemData,
  UpdateLineItemData,
  InventoryLineItem,
} from '@/types';

export interface HoldRequestsResponse {
  holdRequests: HoldRequest[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  statusCounts?: Record<string, number>;
}

/**
 * Hook to fetch hold requests
 */
export function useHoldRequests(queryString?: string) {
  return useQuery({
    queryKey: ['holdRequests', queryString],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: HoldRequestsResponse }>(`/holds${queryString ? `?${queryString}` : ''}`);
      return response.data;
    },
  });
}

/**
 * Hook to fetch a single hold request
 */
export function useHoldRequest(id: string) {
  return useQuery({
    queryKey: ['holdRequest', id],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: { holdRequest: HoldRequest } }>(`/holds/${id}`);
      return response.data.holdRequest;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a hold request
 */
export function useCreateHoldRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateHoldRequestData & { salesAeId: string }) => {
      const response = await post<{ success: boolean; data: { holdRequest: HoldRequest } }>('/holds', data);
      return response.data.holdRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to update a hold request
 */
export function useUpdateHoldRequest(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CreateHoldRequestData>) => put<HoldRequest>(`/holds/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequest', id] });
    },
  });
}

/**
 * Hook to delete a hold request
 */
export function useDeleteHoldRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => del(`/holds/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to fetch line items for a hold request
 */
export function useLineItems(holdRequestId: string) {
  return useQuery({
    queryKey: ['lineItems', holdRequestId],
    queryFn: async () => {
      const response = await get<{ success: boolean; data: { lineItems: InventoryLineItem[] } }>(`/holds/${holdRequestId}/items`);
      return response.data.lineItems;
    },
    enabled: !!holdRequestId,
  });
}

/**
 * Hook to create a line item
 */
export function useCreateLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { holdRequestId: string } & CreateLineItemData) => {
      const response = await post<{ success: boolean; data: { lineItem: InventoryLineItem } }>('/items', data);
      return response.data.lineItem;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['lineItems', variables.holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequest', variables.holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

/**
 * Hook to update a line item
 */
export function useUpdateLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateLineItemData> }) => {
      const response = await put<{ success: boolean; data: { lineItem: InventoryLineItem } }>(`/items/${id}`, data);
      return response.data.lineItem;
    },
    onSuccess: (lineItem) => {
      queryClient.invalidateQueries({ queryKey: ['lineItems', lineItem.holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequest', lineItem.holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail'] });
    },
  });
}

/**
 * Hook to delete a line item
 */
export function useDeleteLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; holdRequestId: string }) => {
      await del(`/items/${id}`);
      return id;
    },
    onSuccess: (_, variables) => {
      const { id, holdRequestId } = variables;
      queryClient.invalidateQueries({ queryKey: ['lineItems', holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequest', holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['holdRequestLineItems', holdRequestId] });
      queryClient.invalidateQueries({ queryKey: ['lineItemDetail', id] });
      queryClient.invalidateQueries({ queryKey: ['chartingQueue'] });
      queryClient.invalidateQueries({ queryKey: ['chartingStatistics'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionsQueue'] });
      queryClient.invalidateQueries({ queryKey: ['exceptionStatistics'] });
    },
  });
}

/**
 * Hook to bulk update line items
 */
export function useBulkUpdateLineItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { lineItemIds: string[]; updates: UpdateLineItemData }) => {
      const response = await post<{
        success: boolean;
        data: {
          successful: string[];
          failed: Array<{ id: string; error: string }>;
          summary: { total: number; succeeded: number; failed: number };
        };
      }>('/items/bulk-update', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lineItems'] });
      queryClient.invalidateQueries({ queryKey: ['holdRequests'] });
    },
  });
}

